// Chemin: C:\gestion-copro\src\pages\admin\SignalementDetailPage.tsx
// Page de détail d'un signalement pour COPRO PROTECT
// Session 24 : ajout fonctionnalité upload photos
// Session 75 : Sécurité suppression (bouton visible uniquement pour Gestionnaire/Admin SaaS)

import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../services/firebase';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';
import { Signalement, SignalementStatut } from '../../types/signalement.types';
import { generateSignalementDetailPDF } from '../../services/pdf.service';
import AdminLayout from '../../components/layout/AdminLayout';
import { Camera, Upload, X, Loader2 } from 'lucide-react';
import { compressImage } from '../../utils/imageCompressor';

// ============================================
// CONSTANTES
// ============================================

const STATUTS: { value: SignalementStatut; label: string; color: string }[] = [
  { value: 'nouveau', label: 'Nouveau', color: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'en_cours', label: 'En cours', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  { value: 'transmis_syndic', label: 'Transmis syndic', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'transmis_promoteur', label: 'Transmis promoteur', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { value: 'resolu', label: 'Résolu', color: 'bg-green-100 text-green-700 border-green-200' },
  { value: 'refuse', label: 'Refusé', color: 'bg-gray-100 text-gray-700 border-gray-200' },
];

const URGENCES: Record<string, { label: string; color: string; bg: string }> = {
  basse: { label: 'Basse', color: 'text-green-700', bg: 'bg-green-100' },
  moyenne: { label: 'Moyenne', color: 'text-yellow-700', bg: 'bg-yellow-100' },
  haute: { label: 'Haute', color: 'text-orange-700', bg: 'bg-orange-100' },
  critique: { label: 'Critique', color: 'text-red-700', bg: 'bg-red-100' },
};

const LOCALISATIONS: Record<string, string> = {
  parties_communes: 'Parties communes',
  parties_privatives: 'Parties privatives',
  equipements: 'Équipements',
};

// Taille max par photo (5 Mo)
const MAX_FILE_SIZE = 5 * 1024 * 1024;
// Types acceptés
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const SignalementDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { userProfile } = useAuth();
  const { copropriete } = useCopropriete();
  
  // Session 75 : Helper pour la permission de suppression
  const canDeleteSignalement = userProfile?.isGestionnaireSaas === true || 
                               userProfile?.role === 'gestionnaire' || 
                               userProfile?.role === 'admin_saas';
  
  const [signalement, setSignalement] = useState<Signalement | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  
  // État pour l'upload de photos
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>('');
  
  // État pour la suppression de photo
  const [photoToDelete, setPhotoToDelete] = useState<string | null>(null);
  const [deletingPhoto, setDeletingPhoto] = useState(false);

  // Charger le signalement
  useEffect(() => {
    const loadSignalement = async () => {
      if (!id) return;

      try {
        const docRef = doc(db, 'signalements', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setSignalement({
            id: docSnap.id,
            ...docSnap.data()
          } as Signalement);
        } else {
          navigate('/admin/signalements');
        }
      } catch (error) {
        console.error('Erreur chargement signalement:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSignalement();
  }, [id, navigate]);

  // Changer le statut
  const changerStatut = async (nouveauStatut: SignalementStatut) => {
    if (!signalement || !id) return;

    setUpdating(true);
    try {
      const docRef = doc(db, 'signalements', id);
      await updateDoc(docRef, {
        statut: nouveauStatut,
        dateModification: Timestamp.now(),
        historique: [
          ...(signalement.historique || []),
          {
            date: Timestamp.now(),
            action: `Statut changé en "${STATUTS.find((s) => s.value === nouveauStatut)?.label}"`,
            auteur: 'Gestionnaire'
          }
        ]
      });

      setSignalement({
        ...signalement,
        statut: nouveauStatut
      });
    } catch (error) {
      console.error('Erreur mise à jour statut:', error);
      alert('Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  // Export PDF
  const handleExportPDF = async () => {
    if (!signalement) return;

    setExportingPDF(true);
    try {
      await generateSignalementDetailPDF(signalement, copropriete?.nom);
    } catch (error) {
      console.error('Erreur export PDF:', error);
      alert('Erreur lors de la génération du PDF');
    } finally {
      setExportingPDF(false);
    }
  };

  // Supprimer le signalement
  const supprimerSignalement = async () => {
    if (!id) return;

    setUpdating(true);
    try {
      await deleteDoc(doc(db, 'signalements', id));
      navigate('/admin/signalements');
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
      setUpdating(false);
    }
  };

  // === NOUVEAU : Upload de photos ===
  const handlePhotoSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !signalement || !id) return;

    // Valider les fichiers
    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      if (!ACCEPTED_TYPES.includes(file.type)) {
        alert(`Le fichier "${file.name}" n'est pas une image valide (JPG, PNG, WebP acceptés)`);
        continue;
      }
      
      if (file.size > MAX_FILE_SIZE) {
        alert(`Le fichier "${file.name}" est trop volumineux (max 5 Mo)`);
        continue;
      }
      
      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      // Reset l'input
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploadingPhotos(true);
    setUploadProgress(`Compression et upload de ${validFiles.length} photo(s)...`);

    try {
      const newPhotoURLs: string[] = [];

      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        setUploadProgress(`Compression ${i + 1}/${validFiles.length} : ${file.name}`);

        // Compresser l'image avant upload
        let fileToUpload = file;
        try {
          fileToUpload = await compressImage(file, {
            maxWidth: 1200,
            maxHeight: 1200,
            quality: 0.8,
          });
          console.log(`Image compressée: ${(file.size / 1024).toFixed(0)} Ko → ${(fileToUpload.size / 1024).toFixed(0)} Ko`);
        } catch (compressError) {
          console.warn('Compression échouée, upload de l\'original:', compressError);
        }

        setUploadProgress(`Upload ${i + 1}/${validFiles.length} : ${file.name}`);

        // Générer un nom unique
        const timestamp = Date.now();
        const extension = fileToUpload.name.split('.').pop() || 'jpg';
        const fileName = `${timestamp}_${Math.random().toString(36).substring(7)}.${extension}`;

        // Upload vers Storage
        const storageRef = ref(storage, `signalements/${id}/${fileName}`);
        const snapshot = await uploadBytes(storageRef, fileToUpload);
        const downloadURL = await getDownloadURL(snapshot.ref);
        
        newPhotoURLs.push(downloadURL);
      }

      // Mettre à jour Firestore avec les nouvelles photos
      const existingPhotos = signalement.photos || [];
      const allPhotos = [...existingPhotos, ...newPhotoURLs];

      const docRef = doc(db, 'signalements', id);
      await updateDoc(docRef, {
        photos: allPhotos,
        dateModification: Timestamp.now(),
        updatedAt: Timestamp.now(),
        historique: [
          ...(signalement.historique || []),
          {
            date: Timestamp.now(),
            action: `${newPhotoURLs.length} photo(s) ajoutée(s)`,
            auteur: 'Gestionnaire'
          }
        ]
      });

      // Mettre à jour l'état local
      setSignalement({
        ...signalement,
        photos: allPhotos,
        historique: [
          ...(signalement.historique || []),
          {
            date: Timestamp.now(),
            action: `${newPhotoURLs.length} photo(s) ajoutée(s)`,
            auteur: 'Gestionnaire'
          }
        ]
      });

      setUploadProgress('');
    } catch (error) {
      console.error('Erreur upload photos:', error);
      alert('Erreur lors de l\'upload des photos');
    } finally {
      setUploadingPhotos(false);
      // Reset l'input pour permettre de re-sélectionner les mêmes fichiers
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // === NOUVEAU : Suppression de photo ===
  const handleDeletePhoto = async () => {
    if (!photoToDelete || !signalement || !id) return;

    setDeletingPhoto(true);

    try {
      // Essayer de supprimer du Storage (peut échouer si URL externe)
      try {
        const photoRef = ref(storage, photoToDelete);
        await deleteObject(photoRef);
      } catch (storageError) {
        console.warn('Impossible de supprimer du Storage (URL externe ?):', storageError);
      }

      // Mettre à jour Firestore (retirer la photo du tableau)
      const updatedPhotos = signalement.photos.filter(p => p !== photoToDelete);
      
      const docRef = doc(db, 'signalements', id);
      await updateDoc(docRef, {
        photos: updatedPhotos,
        dateModification: Timestamp.now(),
        updatedAt: Timestamp.now(),
        historique: [
          ...(signalement.historique || []),
          {
            date: Timestamp.now(),
            action: 'Photo supprimée',
            auteur: 'Gestionnaire'
          }
        ]
      });

      // Mettre à jour l'état local
      setSignalement({
        ...signalement,
        photos: updatedPhotos,
        historique: [
          ...(signalement.historique || []),
          {
            date: Timestamp.now(),
            action: 'Photo supprimée',
            auteur: 'Gestionnaire'
          }
        ]
      });

      setPhotoToDelete(null);
    } catch (error) {
      console.error('Erreur suppression photo:', error);
      alert('Erreur lors de la suppression de la photo');
    } finally {
      setDeletingPhoto(false);
    }
  };

  // Formater la date
  const formatDate = (timestamp: any): string => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!signalement) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-gray-500">Signalement non trouvé</p>
          <Link to="/admin/signalements" className="text-blue-600 mt-4 inline-block">
            ← Retour à la liste
          </Link>
        </div>
      </AdminLayout>
    );
  }

  const statutConfig = STATUTS.find((s) => s.value === signalement.statut);
  const urgenceConfig = URGENCES[signalement.urgence];
  const hasPhotos = signalement.photos && signalement.photos.length > 0;

  return (
    <AdminLayout>
      {/* Input file caché */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        multiple
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Header */}
      <div className="mb-6">
        <Link 
          to="/admin/signalements" 
          className="text-gray-500 hover:text-gray-700 text-sm mb-2 inline-block"
        >
          ← Retour à la liste
        </Link>
        
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">{signalement.titre}</h1>
            <p className="text-gray-500">
              Signalé le {formatDate(signalement.dateSignalement)}
            </p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {/* Badge urgence */}
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${urgenceConfig?.bg} ${urgenceConfig?.color}`}>
              {urgenceConfig?.label}
            </span>
            
            {/* Sélecteur de statut */}
            <select
              value={signalement.statut}
              onChange={(e) => changerStatut(e.target.value as SignalementStatut)}
              disabled={updating}
              className={`
                px-4 py-1 rounded-full text-sm font-medium border cursor-pointer
                ${statutConfig?.color}
                ${updating ? 'opacity-50' : ''}
              `}
            >
              {STATUTS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colonne principale */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="font-semibold text-gray-800 mb-4">Description</h2>
            <p className="text-gray-600 whitespace-pre-wrap">{signalement.description}</p>
          </div>

          {/* Photos - Section toujours visible avec bouton d'ajout */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-800">
                Photos {hasPhotos && `(${signalement.photos.length})`}
              </h2>
              <button
                onClick={handlePhotoSelect}
                disabled={uploadingPhotos}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
              >
                {uploadingPhotos ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Upload...
                  </>
                ) : (
                  <>
                    <Camera className="w-4 h-4" />
                    Ajouter des photos
                  </>
                )}
              </button>
            </div>

            {/* Message de progression */}
            {uploadingPhotos && uploadProgress && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                {uploadProgress}
              </div>
            )}

            {/* Grille de photos ou message si aucune */}
            {hasPhotos ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {signalement.photos.map((photo, index) => (
                  <div key={index} className="relative">
                    <button
                      onClick={() => setSelectedImage(photo)}
                      className="aspect-square rounded-lg overflow-hidden hover:opacity-90 transition-opacity w-full"
                    >
                      <img
                        src={photo}
                        alt={`Photo ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                    {/* Bouton supprimer (toujours visible) */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setPhotoToDelete(photo);
                      }}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full shadow-md hover:bg-red-600 transition-colors"
                      title="Supprimer cette photo"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                <Upload className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm mb-2">
                  Aucune photo pour le moment
                </p>
                <p className="text-gray-400 text-xs">
                  Ajoutez des photos pour renforcer votre dossier
                </p>
              </div>
            )}
          </div>

          {/* Historique */}
          {signalement.historique && signalement.historique.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="font-semibold text-gray-800 mb-4">Historique</h2>
              <div className="space-y-3">
                {signalement.historique.map((entry, index) => (
                  <div key={index} className="flex gap-3 text-sm">
                    <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0"></div>
                    <div>
                      <p className="text-gray-800">{entry.action}</p>
                      <p className="text-gray-400 text-xs">
                        {formatDate(entry.date)}
                        {entry.auteur && ` • ${entry.auteur}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Colonne latérale */}
        <div className="space-y-6">
          {/* Informations */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="font-semibold text-gray-800 mb-4">Informations</h2>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm text-gray-500">Localisation</dt>
                <dd className="text-gray-800">
                  {LOCALISATIONS[signalement.localisation]}
                </dd>
                <dd className="text-gray-600 text-sm">
                  {signalement.localisationDetail}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Catégorie</dt>
                <dd className="text-gray-800">{signalement.categorie}</dd>
              </div>
              {signalement.numeroSignalement && (
                <div>
                  <dt className="text-sm text-gray-500">Numéro</dt>
                  <dd className="text-gray-800 font-mono text-sm">
                    {signalement.numeroSignalement}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Déclarant */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="font-semibold text-gray-800 mb-4">Déclarant</h2>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm text-gray-500">Nom</dt>
                <dd className="text-gray-800">{signalement.declarant.nom}</dd>
              </div>
              {signalement.declarant.email && (
                <div>
                  <dt className="text-sm text-gray-500">Email</dt>
                  <dd>
                    <a 
                      href={`mailto:${signalement.declarant.email}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {signalement.declarant.email}
                    </a>
                  </dd>
                </div>
              )}
              {signalement.declarant.telephone && (
                <div>
                  <dt className="text-sm text-gray-500">Téléphone</dt>
                  <dd>
                    <a 
                      href={`tel:${signalement.declarant.telephone}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {signalement.declarant.telephone}
                    </a>
                  </dd>
                </div>
              )}
              {signalement.declarant.lot && (
                <div>
                  <dt className="text-sm text-gray-500">Lot</dt>
                  <dd className="text-gray-800">{signalement.declarant.lot}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="font-semibold text-gray-800 mb-4">Actions</h2>
            <div className="space-y-2">
              <button
                onClick={handleExportPDF}
                disabled={exportingPDF}
                className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-3 disabled:opacity-50"
              >
                <span>📄</span>
                <span className="text-gray-700">
                  {exportingPDF ? 'Génération...' : 'Exporter en PDF'}
                </span>
              </button>
              <button
                onClick={() => alert('Génération de courrier - À implémenter')}
                className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-3"
              >
                <span>📝</span>
                <span className="text-gray-700">Générer un courrier</span>
              </button>
              {/* Session 75 : Bouton Supprimer visible uniquement pour Gestionnaire/Admin SaaS */}
              {canDeleteSignalement && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full text-left px-4 py-3 rounded-lg hover:bg-red-50 transition-colors flex items-center gap-3 text-red-600"
                >
                  <span>🗑️</span>
                  <span>Supprimer</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de confirmation de suppression */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              Confirmer la suppression
            </h3>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer ce signalement ? Cette action est irréversible.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={supprimerSignalement}
                disabled={updating}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {updating ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression de PHOTO */}
      {photoToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              Supprimer cette photo ?
            </h3>
            <p className="text-gray-600 mb-4">
              Cette action est irréversible.
            </p>
            {/* Aperçu de la photo */}
            <div className="mb-4 rounded-lg overflow-hidden max-h-40">
              <img 
                src={photoToDelete} 
                alt="Photo à supprimer" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setPhotoToDelete(null)}
                disabled={deletingPhoto}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleDeletePhoto}
                disabled={deletingPhoto}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {deletingPhoto ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Suppression...
                  </>
                ) : (
                  'Supprimer'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'affichage d'image */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-lg"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={selectedImage}
            alt="Photo agrandie"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </AdminLayout>
  );
};

export default SignalementDetailPage;